import type { MatchClient } from "./match-client";
import type { Dict } from "./i18n";
import { actionName, personaName, resourceKindName } from "./i18n";
import { isIconImage, resolveIcon } from "./icons";
import { formatClock, phaseFor, slotTheme } from "./theme";
import { HudPicker } from "./HudPicker";

export function Hud({
  client,
  locale,
  d,
  onPick,
  waitingSlots = [],
}: {
  client: MatchClient;
  locale: MatchClient["currentLocale"];
  d: Dict;
  /** Swaps the cricket in a slot. Omitted for a replay, which is read-only. */
  onPick?: (slot: 0 | 1, id: string) => void;
  /** Slots currently waiting on a live-model decision (polled by the parent). */
  waitingSlots?: readonly number[];
}) {
  const state = client.current;
  const [a, b] = state.crickets;
  const phase = phaseFor(state.timeRemainingTicks, d);
  const maxScore = Math.max(1, a?.totalScore ?? 0, b?.totalScore ?? 0);

  const side = (
    cricket: (typeof state.crickets)[number] | undefined,
    info: MatchClient["infos"][number],
    align: "left" | "right",
    slot: 0 | 1,
  ) => {
    if (!cricket) return null;
    const theme = slotTheme(cricket.slot);
    const icon = resolveIcon(info.icon, slot, info.id);
    const hpFrac = Math.max(0, cricket.hp / cricket.maxHp);
    const hpColor = hpFrac > 0.5 ? "#34d399" : hpFrac > 0.25 ? "#fbbf24" : "#ef4444";
    return (
      <div className={`hud-side ${align === "right" ? "right" : ""}`}>
        <div
          className="hud-info"
          style={{ flexDirection: align === "right" ? "row-reverse" : "row" }}
        >
          <div
            className="hud-name"
            style={{ flexDirection: align === "right" ? "row-reverse" : "row" }}
          >
            <span
              className="swatch swatch-icon"
              style={{
                background: isIconImage(icon) ? "#ffffff" : theme.color,
                boxShadow: `0 0 8px ${theme.glow}`,
              }}
              aria-hidden
            >
              {isIconImage(icon) ? (
                <img src={icon} alt="" width={14} height={14} />
              ) : (
                <span className="swatch-glyph">{icon}</span>
              )}
            </span>
            <span>{personaName(info.persona, locale)}</span>
            <span className="model">{info.model === "heuristic" ? "BOT" : "AI"}</span>
            {waitingSlots.includes(slot) ? (
              <span className="thinking" title={d.aiThinking}>
                {d.aiThinking}
              </span>
            ) : null}
          </div>
          <div className="hud-score" style={{ color: theme.color }} title={d.totalScoreLabel}>
            {cricket.totalScore}
          </div>
        </div>
        <div className="hud-sub">
          {d.hp} {cricket.hp} / {cricket.maxHp} · ◆ {cricket.score}
          {cricket.bombs + cricket.blades > 0
            ? ` · ${d.inventoryTag(cricket.bombs, cricket.blades)}`
            : ""}
          {cricket.collecting ? ` · ${d.collecting}` : ""}
          {cricket.defending ? ` · ${d.defending}` : ""}
          {cricket.fleeing ? ` · ${actionName("FLEE", locale)}` : ""}
          {cricket.bladeActive ? ` · ${resourceKindName("blade", locale)}` : ""}
          {!cricket.alive ? ` · ${d.eliminatedBadge}` : ""}
        </div>
        <div className="hud-bar hud-bar-hp" title={`${d.hp} ${cricket.hp}/${cricket.maxHp}`}>
          <i style={{ width: `${hpFrac * 100}%`, background: hpColor }} />
        </div>
        <div className="hud-bar hud-bar-score" title={`${d.totalScoreLabel} ${cricket.totalScore}`}>
          <i style={{ width: `${(cricket.totalScore / maxScore) * 100}%`, background: theme.color }} />
        </div>
        {onPick ? (
          <HudPicker slot={slot} currentId={info.id} onPick={(id) => onPick(slot, id)} d={d} />
        ) : null}
      </div>
    );
  };

  return (
    <div className="hud">
      {side(a, client.infos[0], "left", 0)}
      <div className="hud-center">
        <div className="hud-clock mono">{formatClock(state.timeRemainingTicks)}</div>
        <div className={`hud-phase ${phase.final ? "final" : ""}`}>{phase.label}</div>
        <div className="hud-phase" style={{ marginTop: 4 }}>
          <span className={`status-dot ${client.finished ? "" : "live"}`} />
          {client.finished ? d.ended : d.live}
        </div>
      </div>
      {side(b, client.infos[1], "right", 1)}
    </div>
  );
}
